/**
 * CR-6441 re-test (7 Sep): the Company section's Site and Division lists.
 *
 * The rework sourced both from `/purchasing/lines/filter-options/`, whose
 * option lists are built from `po__customer = profile.company` — the user's
 * HOME company only. A customer user with several accessible sites, or a
 * group view, was offered one site and one division, so the Division → Site
 * cascade had nothing to narrow and looked broken. Before the rework the
 * Site dropdown came from `/core/sites/accessible/`, which honours the
 * user's actual access set and carries the group and country the cascade
 * needs.
 *
 * These helpers union the two: every accessible site is offered (as before),
 * and the line-options entries keep the cascade fields for any site that
 * appears in both.
 */
import type { ASNCascadeFilterOptions } from './useASNFilterCascades';

export type CascadeSite = ASNCascadeFilterOptions['sites'][number];
export type DivisionChoice = ASNCascadeFilterOptions['divisions'][number];

/** A site as `/core/sites/accessible/` serialises it (the fields used here). */
export interface AccessibleSiteLike {
  id: number;
  name: string;
  group_id?: number | null;
  group_name?: string | null;
  country?: string | null;
}

/**
 * Union of the line-options customers and the user's accessible sites,
 * keyed by id, sorted by name. Line-options entries win on a clash (they are
 * the ones the other Purchasing tabs cascade on).
 *
 * @param lineSites - `customers` from the line filter options.
 * @param accessible - Sites from the accessible-sites endpoint.
 * @returns One cascade-ready site list.
 */
export function mergeCascadeSites(
  lineSites: ReadonlyArray<CascadeSite>,
  accessible: ReadonlyArray<AccessibleSiteLike>,
): CascadeSite[] {
  const byId = new Map<number, CascadeSite>();
  for (const s of accessible) {
    byId.set(s.id, { id: s.id, name: s.name, group: s.group_id ?? null, country: s.country ?? null });
  }
  for (const s of lineSites) {
    byId.set(s.id, { id: s.id, name: s.name, group: s.group ?? null, country: s.country ?? null });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Union of the line-options divisions and the groups the accessible sites
 * belong to, keyed by id, sorted by name.
 *
 * @param lineDivisions - `divisions` from the line filter options.
 * @param accessible - Sites from the accessible-sites endpoint.
 * @returns One division list for the Division dropdown.
 */
export function mergeDivisions(
  lineDivisions: ReadonlyArray<DivisionChoice>,
  accessible: ReadonlyArray<AccessibleSiteLike>,
): DivisionChoice[] {
  const byId = new Map<number, DivisionChoice>();
  for (const s of accessible) {
    if (s.group_id != null && s.group_name) byId.set(s.group_id, { id: s.group_id, name: s.group_name });
  }
  for (const d of lineDivisions) {
    byId.set(d.id, { id: d.id, name: d.name });
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Distinct, sorted countries across both sources.
 *
 * @param lineCountries - `site_countries` from the line filter options.
 * @param sites - The merged cascade sites.
 * @returns Sorted distinct country values.
 */
export function mergeSiteCountries(
  lineCountries: ReadonlyArray<string>,
  sites: ReadonlyArray<CascadeSite>,
): string[] {
  const all = new Set<string>(lineCountries.filter(Boolean));
  for (const s of sites) if (s.country) all.add(s.country);
  return Array.from(all).sort();
}
